
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Download, RefreshCw, Trash2, LogOut } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface DashboardHeaderProps {
  onRefresh: () => void;
  onExportCSV: () => void;
  onDeleteAll: () => void;
  refreshing: boolean;
  deletingAll: boolean;
  totalLeads: number;
}

export const DashboardHeader = ({
  onRefresh,
  onExportCSV,
  onDeleteAll,
  refreshing,
  deletingAll,
  totalLeads
}: DashboardHeaderProps) => {
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast({
        title: "Error",
        description: "No se pudo cerrar la sesión",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión correctamente",
      });
    }
  };

  return (
    <div className="mb-6 md:mb-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-center md:text-left">
          <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-1 md:mb-2">
            Early Access Leads
          </h1>
          <p className="text-sm md:text-base text-gray-600">
            Administrative panel to view Show Up! registrations • {user?.email}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 md:gap-2">
          <Button 
            onClick={onRefresh} 
            variant="outline"
            disabled={refreshing}
            className="flex items-center justify-center gap-2 text-sm md:text-base h-9 md:h-10"
          >
            <RefreshCw className={`h-3 w-3 md:h-4 md:w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={onExportCSV} 
            className="flex items-center justify-center gap-2 text-sm md:text-base h-9 md:h-10"
          >
            <Download className="h-3 w-3 md:h-4 md:w-4" />
            Export CSV
          </Button>
          
          {totalLeads > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  className="flex items-center justify-center gap-2 text-sm md:text-base h-9 md:h-10"
                >
                  <Trash2 className="h-3 w-3 md:h-4 md:w-4" />
                  Delete All
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="mx-4 max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete All Records</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete all {totalLeads} records? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDeleteAll}
                    disabled={deletingAll}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {deletingAll ? "Deleting..." : "Delete All"}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          <Button 
            onClick={handleSignOut} 
            variant="outline"
            className="flex items-center justify-center gap-2 text-sm md:text-base h-9 md:h-10"
          >
            <LogOut className="h-3 w-3 md:h-4 md:w-4" />
            Logout
          </Button>
        </div>
      </div>
    </div>
  );
};
